<template>
  <div>
    <div v-if='this.loading'>
      <v-row
        align='center'
        justify='center'
        class='custom-margin'
      >
        <v-progress-circular
          size='25'
          color='primary'
          indeterminate
        />
      </v-row>
    </div>

    <div v-show='!this.loading'>
      <v-alert
        v-if='this.stripeNotAvailable'
        type='error'
        class='custom-margin'
      >
        {{ $t('order.payment.error.general') }}
      </v-alert>

      <v-alert
        v-else-if='this.already_paid'
        type='success'
        class='custom-margin'
      >
        {{ $t('order.payment.already_paid') }}
      </v-alert>

      <v-row v-else>
        <v-col cols='12' class='main-container'>
          <div class='container-credit-card'>
            <v-text-field
              v-model='card_holder'
              class='custom_input_name custom_placeholder'
              :placeholder='this.$t("order.payment.label_name")'
              @input='checkCard'
            />
            <div id='card-elements' class='form-control'/>
            <span class='card-error red--text'>
              {{ cardErrorMessage }}
            </span>
            <v-img
              :src='card_image'
              width='100'
              alt='accepted cards'
            />
          </div>

          <v-btn
            color='primary'
            class='text-capitalize'
            :disabled='submitDisabled'
            :loading='this.loading'
            @click='initPayment'
          >
            {{ $t('order.payment.pay_now')}}
          </v-btn>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { stripeMixin } from '../../mixins/stripe_mixin'

export default {
  name: 'StripeCardElement',
  props: {
    stripe_publishable_key: {type: String},
    stripe_account: {type: String},
    already_paid: {type: Boolean},
    uuid: {type: String},
    pay_url: {type: String}
  },
  mixins: [
    stripeMixin
  ],
  data() {
    return {
      stripe: null,
      card: null,
      card_holder: null,
      loading: true,
      cardInputMounted: false,
      stripeNotAvailable: null,
      submitDisabled: true,
      cardValid: false,
      cardErrorMessage: null,
      paymentMethodId: null
    }
  },
  mounted() {
    this.loading = false
  },
  updated() {
    if (!this.cardInputMounted) {
      this.cardInputMounted = true

      if (window.Stripe) {
        this.initStripeInput()
      } else {
       this.stripeNotAvailable = true
      }
    }
  },
  methods: {
    initStripeInput(){
      const stripeAccount = this.stripe_account
      const stripeKey = this.stripe_publishable_key

      if (!!stripeAccount) {
        this.stripe = Stripe(stripeKey, { stripeAccount })
      } else {
        this.stripe = Stripe(stripeKey)
      }

      const elements = this.stripe.elements({ locale: sessionStorage.getItem('guestLocale') })
      this.card = elements.create('card', { hidePostalCode: true })

      if (document.getElementById('card-elements')) {
        this.card.mount('#card-elements')
      }

      // Handle real-time validation errors from the card Element.
      this.card.addEventListener('change', event => {
        if (event.complete) {
          this.cardValid = true
          if (this.card_holder){
            this.submitDisabled = false
            this.cardErrorMessage = null
          } else {
            this.cardErrorMessage = this.$t('order.payment.error.payment_method_id_missing')
          }
        } else if (event.error) {
          this.cardValid = false
          this.submitDisabled = true
          this.cardErrorMessage = event.error.message
        } else {
          this.cardValid = false
          this.submitDisabled = true
        }
      })
    },
    initPayment() {
      if (this.submitDisabled || !this.stripe || !this.card) return
      this.loading = true
      this.submitDisabled = true

      this.stripe.createPaymentMethod('card', this.card)
        .then(result => {
          this.setPaymentMethod(result)
        })
        .catch(error => {
          this.cardErrorMessage = error.message
          this.submitDisabled = false
          this.loading = false
        })
    },
    setPaymentMethod(result) {
      if (result.paymentMethod && result.paymentMethod.id) {
        this.paymentMethodId = result.paymentMethod.id
        this.finalizePayment()
      } else {
        this.cardErrorMessage = this.$t('order.payment.error.payment_method_id_missing')
        this.submitDisabled = false
        this.loading = false
      }
    },
    finalizePayment() {
      const payload = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this.getCsrfToken() },
        body: JSON.stringify({ payment_method_id: this.paymentMethodId, card_holder: this.card_holder })
      }

      fetch(this.pay_url, payload)
        .then((response) => response.json())
        .then((data) => {
          if (data.error){
           throw new Error(data.message)
          }else if(data.extra_info && data.extra_info.requires_action) {
            this.manageAction(data.extra_info)
          }else{
            this.loading = false
            this.submitDisabled = false
            this.$emit('update-paid-state', true)
           }
        })
        .catch(error => {
          console.log('error', error)
          this.cardErrorMessage = error.message
          this.submitDisabled = false
          this.loading = false
        })
    },
    checkCard(){
      if(this.card_holder && this.cardValid){
        this.submitDisabled = false
        this.cardErrorMessage = null
      }else{
        this.submitDisabled = true
      }
    },
    manageAction(response){
      let _this = this
      let confirmURL = response.confirm_url
      let rollbackURL = response.rollback_url
      this.stripe.handleCardAction(response.payment_intent_client_secret).then(result => {
        if (result.error){
          fetch(rollbackURL, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': _this.getCsrfToken() },
            body: JSON.stringify({ payment_intent_id: result.error.payment_intent.id, error_message: result.error.message })
          })
          _this.loading = false
          _this.submitDisabled = false
          _this.cardErrorMessage = result.error.message
        } else {
          const payload = {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': _this.getCsrfToken() },
            body: JSON.stringify({ payment_intent_id: result.paymentIntent.id })
          }
          fetch(confirmURL, payload)
            .then(response => {
              _this.loading = false
              _this.submitDisabled = false
              if (response.ok) {
                _this.$emit('update-paid-state', true)
              } else {
                return response.json()
              }
            })
            .then(responseJson => {
              _this.cardErrorMessage = responseJson.error
            })
            .catch(error => {
              // generic error
              _this.cardErrorMessage = this.$t('order.payment.error.payment_method_id_missing')
            })
        }
      })
    },
    getCsrfToken() {
      return document.querySelector('meta[name="csrf-token"]').content
    }
  }
}
</script>

<style>
.main-container{
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

div.theme--light.v-text-field.custom_input_name>.v-input__control>.v-input__slot:before{
  border-color: rgba(0, 0, 0, .12)!important;
}
div.theme--light.custom_input_name.v-text-field:hover>.v-input__control>.v-input__slot:before{
  border-color: rgba(0, 0, 0, .12)!important;
}
div.v-input--is-focused.v-text-field.custom_input_name>.v-input__control>.v-input__slot:after{
  -webkit-transform: scaleX(0)!important;
  transform: scaleX(0)!important;
}
div.custom_input_name.primary--text{
  color: rgba(0, 0, 0, .87)!important;
  caret-color: rgba(0, 0, 0, .87)!important;
}
.custom_input_name input{
  font-size: 14px;
}
.container-credit-card{
  display: flex;
  flex-direction: column;
  min-width: 250px;
  width: 50%;
}
.container-credit-card img{
  width: 25%;
}
.custom-margin{
  margin-top: 10px;
}
#card-elements{
  padding: 10px 16px;
  border-bottom: 0px solid rgba(0, 0, 0, .12);
  margin-bottom: 10px;
}
#card-elements.StripeElement{
  border-bottom: 1px solid rgba(0, 0, 0, .12);
  transition: all .3s cubic-bezier(.25,.46,.45,.94)
}
.card-error {
  text-align: left;
  margin-bottom: 10px;
}
.custom_placeholder::placeholder {
  color: #d3d3d3;
  opacity: 1;
  text-trasform: capitalize;
}
.custom_placeholder:-ms-input-placeholder {
  color: #d3d3d3;
  text-trasform: capitalize;
}
.custom_placeholder::-ms-input-placeholder {
  color: #d3d3d3;
  text-trasform: capitalize;
}

@media screen and (max-width: 715px){
  .main-container{
    justify-content: end;
  }
  .container-credit-card{
    width: 100%;
  }
}
</style>
